import { useEffect, useRef } from 'react';
import { usePassageState } from '@/lib/hooks/use-passage-state';
import { updateReadingUrl, saveScrollPosition } from '@/lib/utils/scroll-position';
import type { BibleBook, VerseReference } from '@/lib/utils/bible-utils';

// Debug logging flag
const DEBUG_SCROLL_PERSISTENCE = false;

interface UseScrollPositionPersistenceParams {
    currentVerse: VerseReference | null;
}

/**
 * Custom hook to persist scroll position based on visible verse.
 * Updates URL and PassageState when the visible verse changes.
 * Saves to localStorage on page unload for fallback.
 */
export function useScrollPositionPersistence({ currentVerse }: UseScrollPositionPersistenceParams) {
    const { setScrollState } = usePassageState();
    const lastVerseRef = useRef<string | null>(null);
    const isInitialLoadRef = useRef(true);

    // Update URL and PassageState when verse changes
    useEffect(() => {
        if (!currentVerse) {
            if (DEBUG_SCROLL_PERSISTENCE) {
                console.log('[useScrollPositionPersistence] No currentVerse, skipping update');
            }
            return;
        }

        const verseKey = `${currentVerse.book}:${currentVerse.chapter}:${currentVerse.verse}`;

        // Avoid unnecessary updates if verse hasn't changed
        if (verseKey === lastVerseRef.current) {
            if (DEBUG_SCROLL_PERSISTENCE) {
                console.log('[useScrollPositionPersistence] Verse unchanged, skipping update:', verseKey);
            }
            return;
        }

        // Skip URL update on initial load to preserve URL parameters
        // This prevents overwriting ?verse=6 with ?verse=1 when the page first loads
        if (isInitialLoadRef.current) {
            if (DEBUG_SCROLL_PERSISTENCE) {
                console.log('[useScrollPositionPersistence] Initial load detected, skipping URL update:',
                    `${currentVerse.book} ${currentVerse.chapter}:${currentVerse.verse}`
                );
            }
            lastVerseRef.current = verseKey;
            isInitialLoadRef.current = false;

            // Still update PassageState for header display
            const book = currentVerse.book as BibleBook;
            const { chapter, verse } = currentVerse;
            setScrollState({book, chapter, verse});
            return;
        }

        if (DEBUG_SCROLL_PERSISTENCE) {
            console.log('[useScrollPositionPersistence] ✅ Updating URL and state:',
                `${currentVerse.book} ${currentVerse.chapter}:${currentVerse.verse}`,
                `(previous: ${lastVerseRef.current || 'none'})`
            );
        }

        lastVerseRef.current = verseKey;

        const book = currentVerse.book as BibleBook;
        const { chapter, verse } = currentVerse;

        // Update PassageState (for header display)
        setScrollState({book, chapter, verse});

        // Update URL (single source of truth)
        updateReadingUrl(book, chapter, verse);
    }, [currentVerse, setScrollState]);

    // Save to localStorage on page unload (for fallback when returning to app)
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (currentVerse) {
                if (DEBUG_SCROLL_PERSISTENCE) {
                    console.log('[useScrollPositionPersistence] Saving to localStorage on unload:',
                        `${currentVerse.book} ${currentVerse.chapter}:${currentVerse.verse}`
                    );
                }
                saveScrollPosition(
                    currentVerse.book as BibleBook,
                    currentVerse.chapter,
                    currentVerse.verse
                );
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [currentVerse]);
}
